const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/login.ts';
import Imm from 'immutable';
import { LoginData, LoginError, LoginState } from 'App/defs/loginDefs';
import { Logger } from '@cistec/common_web/services/KisLoggerService';
import {
    ActionHandler,
    IActionHandlersMap,
    IKisApiHandlersMap,
    makeReducer,
    ReducerState,
} from '@cistec/common_web/defs/actionTypes';
import {
    ACTION_TYPE_RESET_LOGIN_STATE,
    ACTION_TYPE_RESET_SESSION_STORAGE,
    ACTION_TYPE_SET_LOGIN_DATA_AT_AUTO_LOGIN,
    GET_REGISTER_WORK_ITEM,
    REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN,
    REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION,
} from 'App/actions/fixme.login.actionTypes';
import {
    GetTokenFromSmsCodeAction,
    SendSmsFromInvitationAction,
    SetLoginDataAtAutoLoginAction,
} from 'App/actions/fixme.login';
import { IRefreshTokenAction, REFRESH_TOKEN } from '@cistec/common_web/actions/userActions';
import { createSelector } from 'reselect';
import { BOOK_APPOINTMENT, CANCEL_APPOINTMENT } from 'App/actions/appointment';
import { nativeAccessToken } from './nativeAppAuth';
import { PatAppStoreState } from './setup/rootReducer';

const Log = Logger('loginReducer');

/* PREVIOUSLY LOGIN */

const initialLoginState = Imm.fromJS({
    state: LoginState.INITIAL,
    error: LoginError.NONE,
    gatewayStatus: '',
    mobileNumber: '',
    token: {},
});

const sendSmsFromInvitationHandlers: IKisApiHandlersMap<SendSmsFromInvitationAction> & {
    reset: ActionHandler<GetTokenFromSmsCodeAction>;
} = {
    pending: () => {
        return initialLoginState.set('state', LoginState.SENDING_SMS_FROM_INVITATION);
    },
    success: (state, action) => {
        const payload = Imm.fromJS(action.payload || {});

        const mobileNumber = payload.get('mobileNumber');

        return state
            .set('invitationCode', action.meta.invitationCode)
            .set('mobileNumber', mobileNumber)
            .set('state', LoginState.SMS_SENT)
            .set('attemptsLeft', parseInt(payload.get('attemptsLeft')))
            .set('codeCreationDate', payload.get('creationDate'))
            .set('codeLifeTime', parseInt(payload.get('codeLifeTime')));
    },
    failure: (state, action) => {
        Log.warn('sendSmsFromInvitationFailure', action);
        const payload = action.payload || {};

        const error = payload?.returnedValue?.err || payload?.err || LoginError.UNKNOWN_ERROR;
        return state.set('state', LoginState.SEND_SMS_FAILED).set('error', error);
    },
    reset: () => {
        return initialLoginState;
    },
};

const getTokenFromSmsCodeHandlers: IKisApiHandlersMap<GetTokenFromSmsCodeAction> = {
    pending: state => {
        return state.set('state', LoginState.VERIFYING_SMSCODE).set('error', LoginError.NONE);
    },
    success: (state, action) => {
        const payload = Imm.fromJS(action.payload || {});
        const token = payload.delete('kisim');
        const patnr = payload.getIn(['kisim', 'patnr']);

        // invitationCode need to be set, so we know that it is logged in by testPatient
        const invitationCode = payload.getIn(['kisim', 'invitationCode']);

        const error = (() => {
            if (!token || !token.get('access_token')) {
                return LoginError.MISSING_TOKEN_IN_RESPONSE;
            } else if (!patnr) {
                return LoginError.MISSING_PATNR_IN_RESPONSE;
            } else {
                return LoginError.NONE;
            }
        })();

        if (error !== LoginError.NONE) {
            Log.warn(`getTokenFromAuthCodeSuccess not successful: ${error}`);
            sessionStorage.removeItem(LoginData);
        } else {
            sessionStorage.setItem(LoginData, JSON.stringify(payload));
        }

        return state
            .set('state', error === LoginError.NONE ? LoginState.GOT_TOKEN : LoginState.VERIFY_SMSCODE_FAILED)
            .set('error', error)
            .set('token', token)
            .set('invitationCode', invitationCode);
    },
    failure: (state, action) => {
        Log.warn('getTokenFromAuthCodeFailure', action);
        const payload = action.payload || {};

        const error = payload?.returnedValue?.err || payload?.err || LoginError.UNKNOWN_ERROR;
        return state
            .set('state', LoginState.VERIFY_SMSCODE_FAILED)
            .set('error', error)
            .set('attemptsLeft', parseInt(payload?.returnedValue?.details?.attemptsLeft || 0));
    },
};

const getRegisterWorkItemHandlers: IKisApiHandlersMap<GetTokenFromSmsCodeAction> = {
    pending: state => {
        return state.set('state', LoginState.REGISTER_AWAIT_RESPONSE).set('error', LoginError.NONE);
    },
    success: (state, action) => {
        const immutablePayload = Imm.fromJS(action.payload || {});

        const workId = immutablePayload.getIn(['kisim', 'workId']);
        const patRegisterWorkId = immutablePayload.getIn(['kisim', 'patRegisterWorkId']);
        const patnr = immutablePayload.getIn(['kisim', 'patnr']);
        const userDetails = immutablePayload.getIn(['kisim', 'userDetails']);
        const token = immutablePayload.getIn(['access_token']);

        sessionStorage.setItem(LoginData, JSON.stringify(immutablePayload));
        return state
            .set('state', LoginState.REGISTER_GOT_RESPONSE)
            .set('userDetails', userDetails)
            .set('workId', workId)
            .set('patRegisterWorkId', patRegisterWorkId)
            .set('patnr', patnr)
            .set('error', LoginError.NONE)
            .set('token', { access_token: token });
    },
    failure: (state, action) => {
        Log.warn('getRegisterWorkItemHandlers Failure', action);
        const payload = Imm.fromJS(action.payload || {});
        const error = payload.get('err') || LoginError.UNKNOWN_ERROR;
        return state.set('state', LoginState.REGISTER_RESPONSE_ERROR).set('error', error);
    },
};

const refreshTokenHandlers: IKisApiHandlersMap<IRefreshTokenAction> = {
    pending: state => {
        return state
            .set('state', LoginState.REFRESHING_TOKEN)
            .set('error', LoginError.NONE)
            .setIn(['token', 'access_token'], '');
    },
    success: (state, action) => {
        const payload = Imm.fromJS(action.payload || {});
        const accessToken = payload.get('access_token');
        const error = !accessToken ? LoginError.MISSING_TOKEN_IN_RESPONSE : LoginError.NONE;

        if (error !== LoginError.NONE) {
            Log.warn(`refreshTokenSuccess not successful: ${error}`);
            sessionStorage.removeItem(LoginData);
        } else {
            const loginDat = JSON.parse(sessionStorage.getItem(LoginData) as string);
            if (loginDat) {
                sessionStorage.setItem(LoginData, JSON.stringify({ ...loginDat, access_token: accessToken }));
            }
        }
        return state
            .set('state', error === LoginError.NONE ? LoginState.GOT_TOKEN : LoginState.REFRESH_TOKEN_FAILED)
            .set('error', error)
            .setIn(['token', 'access_token'], accessToken || '');
    },
    failure: (state, action) => {
        Log.warn('refreshTokenFailure', action);
        return state.set('state', LoginState.REFRESH_TOKEN_FAILED).set('error', LoginError.UNKNOWN_ERROR);
    },
};

const setLoginDataAtAutoLoginHandler = (state: ReducerState, action: SetLoginDataAtAutoLoginAction) => {
    const payload = Imm.fromJS(action.payload || {});
    const invitationCode = payload.getIn(['kisim', 'invitationCode']);

    const token = payload.delete('kisim');
    const error = !token ? LoginError.MISSING_TOKEN_IN_RESPONSE : LoginError.NONE;

    return state
        .set('state', error === LoginError.NONE ? LoginState.GOT_TOKEN : LoginState.REFRESH_TOKEN_FAILED)
        .set('error', error)
        .set('token', token || '')
        .set('invitationCode', invitationCode);
};

const resetSessionStorage = (state: ReducerState, _action: any) => {
    sessionStorage.removeItem(LoginData);
    return state;
};

const loginHandlers: IActionHandlersMap = {
    [REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION.pending]: sendSmsFromInvitationHandlers.pending,
    [REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION.success]: sendSmsFromInvitationHandlers.success,
    [REQUEST_ACTION_TYPES_SEND_SMS_FROM_INVITATION.failure]: sendSmsFromInvitationHandlers.failure,

    [REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN.pending]: getTokenFromSmsCodeHandlers.pending,
    [REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN.success]: getTokenFromSmsCodeHandlers.success,
    [REQUEST_ACTION_TYPES_GET_SMS_CODE_TOKEN.failure]: getTokenFromSmsCodeHandlers.failure,

    [ACTION_TYPE_SET_LOGIN_DATA_AT_AUTO_LOGIN]: setLoginDataAtAutoLoginHandler,

    [REFRESH_TOKEN.pending]: refreshTokenHandlers.pending,
    [REFRESH_TOKEN.success]: refreshTokenHandlers.success,
    [REFRESH_TOKEN.failure]: refreshTokenHandlers.failure,

    [GET_REGISTER_WORK_ITEM.pending]: getRegisterWorkItemHandlers.pending,
    [GET_REGISTER_WORK_ITEM.success]: getRegisterWorkItemHandlers.success,
    [GET_REGISTER_WORK_ITEM.failure]: getRegisterWorkItemHandlers.failure,

    // FIXME: this looks incorrect
    [BOOK_APPOINTMENT.success]: (state, action: IRefreshTokenAction) => {
        const payload = Imm.fromJS(action.payload || {});
        const patRegisterWorkId = payload.get('patRegisterWorkId');
        return state.set('patRegisterWorkId', patRegisterWorkId);
    },

    // FIXME: this looks incorrect
    [CANCEL_APPOINTMENT.success]: state => {
        return state.delete('patRegisterWorkId');
    },

    [ACTION_TYPE_RESET_SESSION_STORAGE]: resetSessionStorage,
    [ACTION_TYPE_RESET_LOGIN_STATE]: () => initialLoginState,
};

export const loginReducer = makeReducer(loginHandlers, initialLoginState);

export const loginError = (state: PatAppStoreState): string => state.login.get('error');

export const loginState = (state: PatAppStoreState): string => state.login.get('state');

export const phoneNumber = (state: PatAppStoreState): string => state.login.get('mobileNumber');

export const attemptsLeft = (state: PatAppStoreState): number => parseInt(state.login.get('attemptsLeft'));

export const codeCreationDate = (state: PatAppStoreState): string => state.login.get('codeCreationDate');

export const codeLifeTime = (state: PatAppStoreState): number => parseInt(state.login.get('codeLifeTime'));

export const invitationCode = (state: PatAppStoreState): string => state.login.get('invitationCode');

export const accessToken = (state: PatAppStoreState): string => state.login.getIn(['token', 'access_token']);

export const getPatapiToken = () => {
    const loginDataRaw = sessionStorage.getItem(LoginData);
    const loginData: any = loginDataRaw ? JSON.parse(loginDataRaw) : undefined;
    if (loginData) {
        return loginData.access_token;
    } else {
        return undefined;
    }
};

export const isLoggedIn = (state: PatAppStoreState): boolean => {
    const theLoginState = loginState(state);
    const theAccessToken = getPatapiToken();
    const theNativeAccessToken = nativeAccessToken(state);
    return (theLoginState === LoginState.GOT_TOKEN && !!theAccessToken) || !!theNativeAccessToken;
};

export const selectUserDetailsFromRegisterWorkItem = (state: PatAppStoreState): Imm.Map<string, any> | undefined =>
    state.login.get('userDetails');

// The check for userDetails is technically redundant, but type checking doesn't understand that userDetails has to exist
// and is an immutable map after checking isMap...
export const selectEmailFromRegisterWorkItem = createSelector([selectUserDetailsFromRegisterWorkItem], userDetails =>
    userDetails && Imm.Map.isMap(userDetails) ? userDetails.get('email') : undefined
);

export const patRegisterWorkId = (state: PatAppStoreState): string => state.login.get('patRegisterWorkId');
